import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * The one status pill: 20px, `rounded-full`, 12px text on the tone's soft
 * fill. Labels are title case ("Changes Requested", "Ready to File");
 * relative times ("133d ago") are data and stay as written.
 */
const statusBadgeVariants = cva(
  "inline-flex h-5 w-fit shrink-0 items-center gap-1 rounded-full px-2 text-sm whitespace-nowrap [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0",
  {
    variants: {
      tone: {
        neutral: "bg-fill-2 text-fg-2",
        accent: "bg-accent-soft text-accent-seed",
        success: "bg-success-soft text-success",
        warn: "bg-warn-soft text-warn",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info",
        purple: "bg-purple-soft text-purple",
        orange: "bg-orange-soft text-orange"
      }
    },
    defaultVariants: { tone: "neutral" }
  }
);

type StatusTone = NonNullable<VariantProps<typeof statusBadgeVariants>["tone"]>;

type StatusBadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof statusBadgeVariants> & {
    /** A 6px dot in the tone color before the label. */
    dot?: boolean;
  };

function StatusBadge({
  tone = "neutral",
  dot = false,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      data-slot="status-badge"
      data-tone={tone}
      className={cn(statusBadgeVariants({ tone }), className)}
      {...props}
    >
      {dot ? (
        <span
          className="size-1.5 shrink-0 rounded-full bg-current"
          aria-hidden
        />
      ) : null}
      {children}
    </span>
  );
}

export { StatusBadge, statusBadgeVariants };
export type { StatusBadgeProps, StatusTone };
